<template>
  <div>
    <h5 class="mt-4">{{ $t('gc-rfConfigFormMulti:modems') | capitalize }}</h5>

    <!-- MODEM LIST -->
    <nav class="nav nav-pills mt-2">
      <a v-for="(modem, indexModem) in modems"
         :id="'displayModem' + indexModem"
         :key="indexModem"
         :class="{ active: indexModem === visibleModem }"
         class="nav-link p-2 mr-2"
         href
         @click.prevent="visibleModem = indexModem"
      >
        {{ $t('gc-rfConfigFormMulti:modem') | capitalize }} {{ indexModem }}
      </a>
      <a v-if="modems.length < 8" id="addModem" class="nav-link text-primary p-2" href @click.prevent="addModem">{{ $t('gc-rfConfigFormMulti:addModem') | capitalize }}</a>
      <!-- used to pull the fleet to the right -->
      <div class="d-none d-md-block" style="flex: 1;" />
      <a v-if="modems.length > 1" id="removeModem" class="nav-link text-danger p-2 float-right" href @click.prevent="removeModem">{{ $t('gc-rfConfigFormMulti:removeModem', { modem: visibleModem }) | capitalize }}</a>
    </nav>

    <div v-for="(modem, indexModem) in modems" v-show="indexModem === visibleModem" :key="indexModem" class="mt-2 px-3">
      <!-- MODEM CONFIGURATION -->
      <div class="form-group row mb-0">
        <label :for="'modem'+indexModem+'FrequencyInterval'" class="col-sm-3 col-form-label">{{ $t('gc-rfConfigFormMulti:frequencyInterval') | capitalize }}</label>
        <div class="col-sm-9 py-2">
          <span>{{ round4(Number(modem.board_rx_freq) - modem.board_rx_bw / 2) }} MHz - </span>
          <input :id="'modem'+indexModem+'FrequencyInterval'"
                 v-model="modem.board_rx_freq"
                 v-validate="'required|number'"
                 :class="{ 'is-invalid': errors.has('modem'+indexModem+'FrequencyInterval') }"
                 :data-vv-as="$t('gc-rfConfigFormMulti:frequencyInterval')"
                 :name="'modem'+indexModem+'FrequencyInterval'"
                 type="text"
                 class="form-control form-control-sm px-2"
          > MHz
          <span>- {{ round4(Number(modem.board_rx_freq) + modem.board_rx_bw / 2) }} MHz</span>
          <div class="invalid-feedback">{{ errors.first('modem'+indexModem+'FrequencyInterval') }}</div>
        </div>
      </div>

      <div class="form-group row mb-0">
        <label :for="'modem'+indexModem+'Bandwidth'" class="col-sm-3 col-form-label">{{ $t('gc-rfConfigFormMulti:bandwidth') | capitalize }}</label>
        <div class="col-sm-9 py-2">
          <select :id="'modem'+indexModem+'Bandwidth'" v-model="modem.board_rx_bw" class="form-control form-control-sm w-auto">
            <option :value="4">4</option>
            <option :value="7">7</option>
            <option :value="13">13</option>
          </select> MHz
        </div>
      </div>

      <div v-if="modem.full_duplex !== undefined" class="form-group row mb-0">
        <label class="col-sm-3 col-form-label">{{ $t('gc-rfConfigFormMulti:fullDuplex') | capitalize }}</label>
        <div class="col-sm-9 py-2">
          <input :id="'modem'+indexModem+'FullDuplex'" v-model="modem.full_duplex" type="checkbox">
          <label :for="'modem'+indexModem+'FullDuplex'" />
          <label :for="'modem'+indexModem+'FullDuplex'" class="mr-5">{{ $t('gc-rfConfigFormMulti:enableFullDuplex') | capitalize }}</label>
        </div>
      </div>

      <div v-if="modem.rf_chain_conf[0]" class="form-group row mb-0 align-items-baseline">
        <label class="col-sm-3 col-form-label">{{ $t('gc-rfConfigFormMulti:modemRfChain0') | capitalize }}</label>

        <div class="col-sm-9 d-flex flex-wrap justify-content-between align-items-baseline">
          <div class="py-2">
            <input :id="'modem'+indexModem+'RfChain0DataUp'" v-model="modem.rf_chain_conf[0].rx_enable" type="checkbox">
            <label :for="'modem'+indexModem+'RfChain0DataUp'" />
            <label :for="'modem'+indexModem+'RfChain0DataUp'" class="mr-5">{{ $t('gc-rfConfigFormMulti:modemRfChainDataUp') | capitalize }}</label>
          </div>

          <div class="py-2">
            <input :id="'modem'+indexModem+'RfChain0DataDown'" v-model="modem.rf_chain_conf[0].tx_enable" type="checkbox">
            <label :for="'modem'+indexModem+'RfChain0DataDown'" />
            <label :for="'modem'+indexModem+'RfChain0DataDown'" class="mr-5">{{ $t('gc-rfConfigFormMulti:modemRfChainDataDown') | capitalize }}</label>
          </div>

          <div class="mr-5">
            <label :for="'modem'+indexModem+'RfChain0AntennaGain'" class="form-label mr-2">{{ $t('gc-rfConfigFormMulti:antennaGain') | capitalize }}</label>
            <input :id="'modem'+indexModem+'RfChain0AntennaGain'"
                   v-model="modem.rf_chain_conf[0].antenna_gain"
                   v-validate="'number'"
                   :class="{ 'is-invalid': errors.has('modem'+indexModem+'RfChain0AntennaGain') }"
                   :data-vv-as="$t('gc-rfConfigFormMulti:antennaGain')"
                   :name="'modem'+indexModem+'RfChain0AntennaGain'"
                   type="text"
                   class="form-control form-control-sm px-2"
            > dBi
            <div class="invalid-feedback">{{ errors.first('modem'+indexModem+'RfChain0AntennaGain') }}</div>
          </div>

          <div>
            <label :for="'modem'+indexModem+'RfChain0InsertionLoss'" class="form-label mr-2">{{ $t('gc-rfConfigFormMulti:insertionLoss') | capitalize }}</label>
            <input :id="'modem'+indexModem+'RfChain0InsertionLoss'"
                   v-model="modem.rf_chain_conf[0].insertion_loss"
                   v-validate="'number'"
                   :class="{ 'is-invalid': errors.has('modem'+indexModem+'RfChain0InsertionLoss') }"
                   :data-vv-as="$t('gc-rfConfigFormMulti:insertionLoss')"
                   :name="'modem'+indexModem+'RfChain0InsertionLoss'"
                   type="text"
                   class="form-control form-control-sm px-2"
            > dB
            <div class="invalid-feedback">{{ errors.first('modem'+indexModem+'RfChain0InsertionLoss') }}</div>
          </div>

          <div style="flex: 2;" />
        </div>
      </div>

      <div v-if="modem.rf_chain_conf[1]" class="form-group row mb-0 align-items-baseline">
        <label class="col-sm-3 col-form-label">{{ $t('gc-rfConfigFormMulti:modemRfChain1') | capitalize }}</label>

        <div class="col-sm-9 d-flex flex-wrap justify-content-between align-items-baseline">
          <div class="py-2">
            <input :id="'modem'+indexModem+'RfChain1DataUp'" v-model="modem.rf_chain_conf[1].rx_enable" type="checkbox">
            <label :for="'modem'+indexModem+'RfChain1DataUp'" />
            <label :for="'modem'+indexModem+'RfChain1DataUp'" class="mr-5">{{ $t('gc-rfConfigFormMulti:modemRfChainDataUp') | capitalize }}</label>

            <input :id="'modem'+indexModem+'RfChain1DataDown'" v-model="modem.rf_chain_conf[1].tx_enable" type="checkbox">
            <label :for="'modem'+indexModem+'RfChain1DataDown'" />
            <label :for="'modem'+indexModem+'RfChain1DataDown'" class="mr-5">{{ $t('gc-rfConfigFormMulti:modemRfChainDataDown') | capitalize }}</label>
          </div>

          <div class="mr-5">
            <label :for="'modem'+indexModem+'RfChain1AntennaGain'" class="form-label mr-2">{{ $t('gc-rfConfigFormMulti:antennaGain') | capitalize }}</label>
            <input :id="'modem'+indexModem+'RfChain1AntennaGain'"
                   v-model="modem.rf_chain_conf[1].antenna_gain"
                   v-validate="'number'"
                   :class="{ 'is-invalid': errors.has('modem'+indexModem+'RfChain1AntennaGain') }"
                   :data-vv-as="$t('gc-rfConfigFormMulti:antennaGain')"
                   :name="'modem'+indexModem+'RfChain1AntennaGain'"
                   type="text"
                   class="form-control form-control-sm px-2"
            > dBi
            <div class="invalid-feedback">{{ errors.first('modem'+indexModem+'RfChain1AntennaGain') }}</div>
          </div>

          <div>
            <label :for="'modem'+indexModem+'RfChain1InsertionLoss'" class="form-label mr-2">{{ $t('gc-rfConfigFormMulti:insertionLoss') | capitalize }}</label>
            <input :id="'modem'+indexModem+'RfChain1InsertionLoss'"
                   v-model="modem.rf_chain_conf[1].insertion_loss"
                   v-validate="'number'"
                   :class="{ 'is-invalid': errors.has('modem'+indexModem+'RfChain1InsertionLoss') }"
                   :data-vv-as="$t('gc-rfConfigFormMulti:insertionLoss')"
                   :name="'modem'+indexModem+'RfChain1InsertionLoss'"
                   type="text"
                   class="form-control form-control-sm px-2"
            > dB
            <div class="invalid-feedback">{{ errors.first('modem'+indexModem+'RfChain1InsertionLoss') }}</div>
          </div>

          <div style="flex: 2;" />
        </div>
      </div>

      <!-- CHIP LIST -->
      <nav class="nav nav-pills mt-3">
        <a v-for="(chip, indexChip) in modem.SX1301_conf"
           :id="'displayChip' + indexChip"
           :key="indexChip"
           :class="{ active: indexChip === visibleChip }"
           class="nav-link p-2 mr-2"
           href
           @click.prevent="visibleChip = indexChip"
        >
          {{ $t('gc-rfConfigFormMulti:chip') | capitalize }} {{ indexChip }}
        </a>
      </nav>

      <div v-for="(chip, indexChip) in modem.SX1301_conf" v-show="indexChip === visibleChip" :key="indexChip" class="mt-2 px-3">
        <!-- CHIP CONFIGURATION -->
        <div class="form-group row mb-0">
          <label :for="'modem'+indexModem+'Chip'+indexChip+'FrequencyInterval'" class="col-sm-3 col-form-label">{{ $t('gc-rfConfigFormMulti:frequencyInterval') | capitalize }}</label>
          <div class="col-sm-9 py-2">
            <span>{{ round4(Number(chip.chip_center_freq) - rfConstants.chipBandwidthMhz / 2) }} MHz - </span>
            <input :id="'modem'+indexModem+'Chip'+indexChip+'FrequencyInterval'"
                   v-model="chip.chip_center_freq"
                   v-validate="'required|number'"
                   :class="{ 'is-invalid': errors.has('modem'+indexModem+'Chip'+indexChip+'FrequencyInterval') }"
                   :data-vv-as="$t('gc-rfConfigFormMulti:frequencyInterval')"
                   :name="'modem'+indexModem+'Chip'+indexChip+'FrequencyInterval'"
                   type="text"
                   class="form-control form-control-sm px-2"
            > MHz
            <span>- {{ round4(Number(chip.chip_center_freq) + rfConstants.chipBandwidthMhz / 2) }} MHz</span>
            <div class="invalid-feedback">{{ errors.first('modem'+indexModem+'Chip'+indexChip+'FrequencyInterval') }}</div>
          </div>
        </div>

        <div class="form-group row mb-0">
          <label :for="'modem'+indexModem+'Chip'+indexChip+'RfChain'" class="col-sm-3 col-form-label">{{ $t('gc-rfConfigFormMulti:chipRfChain') | capitalize }}</label>
          <div class="col-sm-9 py-2">
            <select :id="'modem'+indexModem+'Chip'+indexChip+'RfChain'" v-model="chip.chip_rf_chain" :name="'modem'+indexModem+'Chip'+indexChip+'RfChain'" class="form-control form-control-sm">
              <option :value="0">0</option>
              <option :value="1">1</option>
            </select>
          </div>
        </div>

        <!-- CHANNEL CONFIGURATION -->
        <h5 class="mt-3">{{ $t('gc-rfConfigFormMulti:channels') | capitalize }}</h5>

        <div class="table-responsive">
          <table class="table table-sm table-borderless mb-0">
            <thead>
              <tr class="text-center">
                <th>{{ $t('gc-rfConfigFormMulti:name') | capitalize }}</th>
                <th>{{ $t('gc-rfConfigFormMulti:effective') | capitalize }}</th>
                <th>{{ $t('gc-rfConfigFormMulti:spreadingFactor') | capitalize }}</th>
                <th>{{ $t('gc-rfConfigFormMulti:channelFrequency') | capitalize }}</th>
                <th>{{ $t('gc-rfConfigFormMulti:bandwidth') | capitalize }}</th>
              </tr>
            </thead>

            <tbody>
              <!-- Multi SF -->
              <tr v-for="indexChannel in 8" :key="indexChannel" class="text-center">
                <td>{{ 'chan_multiSF_'+(indexChannel-1) }}</td>
                <td>
                  <input :id="'modem'+indexModem+'Chip'+indexChip+'ChannelMultiSf'+(indexChannel-1)+'Enable'"
                         v-model="chip['chan_multiSF_'+(indexChannel-1)].enable"
                         :name="'enableChannel' + indexChannel"
                         type="checkbox"
                  >
                  <label :for="'modem'+indexModem+'Chip'+indexChip+'ChannelMultiSf'+(indexChannel-1)+'Enable'" />
                </td>
                <td>{{ $t('gc-rfConfigFormMulti:multiSf') | capitalize }}</td>
                <td>
                  <input :id="'modem'+indexModem+'Chip'+indexChip+'ChannelMultiSf'+(indexChannel-1)+'ChannelFrequency'"
                         v-model="chip['chan_multiSF_'+(indexChannel-1)].chan_rx_freq"
                         v-validate="{ 'required': chip['chan_multiSF_'+(indexChannel-1)].enable, 'number': true }"
                         :class="{ 'is-invalid': errors.has('modem'+indexModem+'Chip'+indexChip+'ChannelMultiSf'+(indexChannel-1)+'ChannelFrequency') }"
                         :name="'modem'+indexModem+'Chip'+indexChip+'ChannelMultiSf'+(indexChannel-1)+'ChannelFrequency'"
                         :data-vv-as="$t('gc-rfConfigFormMulti:channelFrequency')"
                         type="text"
                         class="form-control form-control-sm"
                  > MHz
                  <div class="invalid-feedback">{{ errors.first('modem'+indexModem+'Chip'+indexChip+'ChannelMultiSf'+(indexChannel-1)+'ChannelFrequency') }}</div>
                </td>
                <td>{{ rfConstants.multiSfBandwidth * 1000 }} kHz</td>
              </tr>

              <!-- Mono SF -->
              <tr v-if="chip.chan_LoRa_std" class="text-center">
                <td>chan_LoRa_std</td>
                <td>
                  <input :id="'modem'+indexModem+'Chip'+indexChip+'ChannelMonoSfEnable'"
                         v-model="chip.chan_LoRa_std.enable"
                         :name="'modem'+indexModem+'Chip'+indexChip+'ChannelMonoSfEnable'"
                         type="checkbox"
                  >
                  <label :for="'modem'+indexModem+'Chip'+indexChip+'ChannelMonoSfEnable'" />
                </td>
                <td>
                  <select :id="'modem'+indexModem+'Chip'+indexChip+'ChannelMonoSfSpreadFactor'"
                          v-model="chip.chan_LoRa_std.spread_factor"
                          class="form-control form-control-sm"
                  >
                    <option :value="7">7</option>
                    <option :value="8">8</option>
                    <option :value="9">9</option>
                    <option :value="10">10</option>
                    <option :value="11">11</option>
                    <option :value="12">12</option>
                  </select>
                </td>
                <td>
                  <input :id="'modem'+indexModem+'Chip'+indexChip+'ChannelMonoSfChannelFrequency'"
                         v-model="chip.chan_LoRa_std.chan_rx_freq"
                         v-validate="{ 'required': chip.chan_LoRa_std.enable, 'number': true }"
                         :class="{ 'is-invalid': errors.has('modem'+indexModem+'Chip'+indexChip+'ChannelMonoSfChannelFrequency') }"
                         :data-vv-as="$t('gc-rfConfigFormMulti:channelFrequency')"
                         :name="'modem'+indexModem+'Chip'+indexChip+'ChannelMonoSfChannelFrequency'"
                         type="text"
                         class="mb-1 form-control form-control-sm"
                  > MHz
                  <div class="invalid-feedback">{{ errors.first('modem'+indexModem+'Chip'+indexChip+'ChannelMonoSfChannelFrequency') }}</div>
                </td>
                <td>
                  <select :id="'modem'+indexModem+'Chip'+indexChip+'ChannelMonoSfBandwidth'"
                          v-model="chip.chan_LoRa_std.bandwidth"
                          class="form-control form-control-sm"
                  >
                    <option value="125000">125</option>
                    <option value="250000">250</option>
                    <option value="500000">500</option>
                  </select> kHz
                </td>
              </tr>

              <!-- FSK -->
              <tr v-if="chip.chan_FSK" class="text-center">
                <td>chan_FSK</td>
                <td>
                  <input :id="'modem'+indexModem+'Chip'+indexChip+'ChannelFskEnable'"
                         v-model="chip.chan_FSK.enable"
                         :name="'modem'+indexModem+'Chip'+indexChip+'ChannelFskEnable'"
                         type="checkbox"
                  >
                  <label :for="'modem'+indexModem+'Chip'+indexChip+'ChannelFskEnable'" />
                </td>
                <td />
                <td>
                  <input :id="'modem'+indexModem+'Chip'+indexChip+'ChannelFskChannelFrequency'"
                         v-model="chip.chan_FSK.chan_rx_freq"
                         v-validate="{ 'required': chip.chan_FSK.enable, 'number': true }"
                         :class="{ 'is-invalid': errors.has('modem'+indexModem+'Chip'+indexChip+'ChannelFskChannelFrequency') }"
                         :data-vv-as="$t('gc-rfConfigFormMulti:channelFrequency')"
                         :name="'modem'+indexModem+'Chip'+indexChip+'ChannelFskChannelFrequency'"
                         type="text"
                         class="mb-1 form-control form-control-sm"
                  > MHz
                  <div class="invalid-feedback">{{ errors.first('modem'+indexModem+'Chip'+indexChip+'ChannelFskChannelFrequency') }}</div>
                </td>
                <td>{{ chip.chan_FSK.bandwidth / 1000 }} kHz</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <h5 v-if="gatewayConf.beacon_enable !== undefined" class="mt-4">{{ $t('gc-rfConfigFormMulti:gateway') | capitalize }}</h5>

    <div v-if="gatewayConf.beacon_enable !== undefined" class="mt-2 px-3">
      <!-- GATEWAY CONFIGURATION -->
      <div class="form-group row mb-0">
        <label class="col-sm-3 col-form-label">{{ $t('gc-rfConfigFormMulti:supportClassB') | capitalize }}</label>
        <div class="py-2">
          <input id="gwBeaconEnable" v-model="gatewayConf.beacon_enable" type="checkbox">
          <label for="gwBeaconEnable" />
          <label for="gwBeaconEnable" class="mr-5">{{ $t('gc-rfConfigFormMulti:beaconEnable') | capitalize }}</label>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { cloneDeep, isEmpty, round, map, each, isObject, isNil, last } from 'lodash-es'
import Vue from 'vue'
import { multi as rfConstants } from '@/configuration/rfConfig'

const emptyModems = [{ rf_chain_conf: [{}, {}], SX1301_conf: [] }]

export default {
  model: {
    prop: 'rfConfig',
    event: 'change'
  },

  props: {
    rfConfig: {
      type: String,
      required: true
    }
  },

  data () {
    return {
      visibleModem: 0,
      visibleChip: 0,

      // Modems configuration
      modems: emptyModems,
      // Gateway configuration
      gatewayConf: {}
    }
  },

  computed: {
    rfConstants () { return rfConstants },

    rfConfigObject () {
      // Return default object before getting data
      if (!this.rfConfig) return { SX1301_array_conf: [], gateway_conf: {} }
      // Remove comments and parse string into object
      return JSON.parse(cloneDeep(this.rfConfig).replace(/\/\*[^*]*\*\//g, ''))
    },

    // Computed property used to watch changes
    formDataProvider () {
      return { modems: [...this.modems], gateway: this.gatewayConf }
    }
  },

  watch: {
    rfConfigObject (newConfig, oldConfig) {
      const isOldValuesEmpty = !oldConfig || (isEmpty(oldConfig.SX1301_array_conf) && isEmpty(oldConfig.gateway_conf))

      // Create formDataProvider only the first time to prevent "change" to be triggered twice
      if (isOldValuesEmpty) {
        this.createFormDataProvider()
      }
    },

    formDataProvider: {
      handler: function (newValues, oldValues) {
        // Create rfConfig string and update v-model
        this.$emit('change', this.recreateRfConfig())

        // Validate form
        this.validateForm()
      },
      deep: true
    },

    visibleModem () {
      this.visibleChip = 0
    }
  },

  created () {
    this.createFormDataProvider()
  },

  methods: {
    // Wrapper to round to be used in template
    round4 (x) {
      return round(x, 4)
    },

    createFormDataProvider () {
      // Return default object before getting data
      if (isEmpty(this.rfConfigObject.SX1301_array_conf)) return

      this.modems = cloneDeep(this.rfConfigObject.SX1301_array_conf)
      this.gatewayConf = cloneDeep(this.rfConfigObject.gateway_conf) || {}

      map(this.modems, modem => {
        // Convert frequencies to MHz
        modem.board_rx_freq = modem.board_rx_freq / 1000000
        // Convert bandwidth to MHz
        modem.board_rx_bw = modem.board_rx_bw / 1000000

        map(modem.SX1301_conf, chip => {
          // Convert frequencies to MHz
          chip.chip_center_freq = chip.chip_center_freq / 1000000

          each(chip, (value, key) => {
            // Convert frequencies to MHz
            if (isObject(chip[key]) && chip[key].chan_rx_freq) chip[key].chan_rx_freq = chip[key].chan_rx_freq / 1000000
            // Set enable value: true if attribute not present
            if (isObject(chip[key]) && isNil(chip[key].enable)) Vue.set(chip[key], 'enable', true)
          })
        })
      })
    },

    recreateRfConfig () {
      const modemsConf = cloneDeep(this.modems)

      // Convert values back to Hz
      map(modemsConf, modem => {
        modem.board_rx_freq = modem.board_rx_freq * 1000000
        modem.board_rx_bw = modem.board_rx_bw * 1000000

        // Convert string to number
        each(modem.rf_chain_conf, rfChain => {
          if (isNil(rfChain.antenna_gain) || rfChain.antenna_gain === '') delete rfChain.antenna_gain
          else rfChain.antenna_gain = Number(rfChain.antenna_gain)

          if (isNil(rfChain.insertion_loss) || rfChain.insertion_loss === '') delete rfChain.insertion_loss
          else rfChain.insertion_loss = Number(rfChain.insertion_loss)
        })

        map(modem.SX1301_conf, chip => {
          chip.chip_center_freq = chip.chip_center_freq * 1000000

          each(chip, (value, key) => {
            if (isObject(chip[key])) {
              if (chip[key].chan_rx_freq) {
                chip[key].chan_rx_freq = chip[key].chan_rx_freq * 1000000
              } else {
                chip[key].chan_rx_freq = null
              }
            }
          })
        })
      })

      return JSON.stringify({ SX1301_array_conf: modemsConf, gateway_conf: this.gatewayConf })
    },

    addModem () {
      // Maximum of 8 modems
      const newModem = cloneDeep(last(this.modems))
      this.modems.push(newModem)
    },

    removeModem () {
      // Minimum of 1 modem
      this.modems.splice(this.visibleModem, 1)
      if (this.visibleModem >= this.modems.length) this.visibleModem = this.modems.length - 1
    },

    async validateForm () {
      // BUGFIX: Need double validations in order to correctly manage channel frequency required constraint depending on channel effectiveness
      await this.$validator.validateAll()
      const valid = await this.$validator.validateAll()

      this.$emit('validate', valid)

      return valid
    }
  }
}
</script>

<style scoped lang="scss">
  .form-control {
    display: inline-block;
  }

  input.form-control {
    width: 55px;
  }

  select.form-control {
    width: 60px;
  }
</style>
